"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Card, CardBody, Badge } from "reactstrap"
import DataTable from "react-data-table-component"
import { Btn } from "../../../AbstractElements"
import {
  type Participation,
  ExhibitorRelatedService,
} from "../../../Service/exhibitorsRelatedService"
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaLayerGroup } from "react-icons/fa"
import Swal from "sweetalert2"

interface Props {
  exhibitorId: number
  onAdd: () => void
  onEdit: (item: Participation) => void
  refresh: boolean
}

const SEARCH_STORAGE_PREFIX = "participations_search_"

const ParticipationsTab: React.FC<Props> = ({
  exhibitorId,
  onAdd,
  onEdit,
  refresh,
}) => {
  const [data, setData] = useState<Participation[]>([])
  const storageKey = `${SEARCH_STORAGE_PREFIX}${exhibitorId}`

  // Restore the last search term the user typed for this exhibitor, so
  // switching tabs, editing a row, or reloading the page doesn't lose it.
  const [search, setSearch] = useState<string>(() => {
    if (typeof window === "undefined") return ""
    try {
      return window.localStorage.getItem(`${SEARCH_STORAGE_PREFIX}${exhibitorId}`) || ""
    } catch {
      return ""
    }
  })
  const [loading, setLoading] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const resp = await ExhibitorRelatedService.getParticipationsByExhibitor(
        exhibitorId
      )
      setData(resp.results)
    } catch (err) {
      console.error("Failed to fetch participations:", err)
      Swal.fire("Error", "Failed to fetch participations", "error")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [exhibitorId, refresh])

  // If the exhibitor changes, load whatever search term was remembered for
  // that exhibitor (or clear it if none was saved yet).
  useEffect(() => {
    if (typeof window === "undefined") return
    try {
      setSearch(window.localStorage.getItem(storageKey) || "")
    } catch {
      setSearch("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exhibitorId])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    try {
      if (value) {
        window.localStorage.setItem(storageKey, value)
      } else {
        window.localStorage.removeItem(storageKey)
      }
    } catch {
      // localStorage may be unavailable (e.g. private browsing) — safe to ignore
    }
  }

  const clearSearch = () => handleSearchChange("")

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "Delete Participation?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      confirmButtonColor: "#dc3545",
    })
    if (!result.isConfirmed) return

    try {
      await ExhibitorRelatedService.deleteParticipation(id)
      await Swal.fire("Deleted!", "Participation removed.", "success")
      fetchData()
    } catch {
      Swal.fire("Error", "Failed to delete participation", "error")
    }
  }

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return data
    return data.filter((item) => {
      return (
        item.exhibition_title?.toLowerCase().includes(term) ||
        item.exhibitor_name?.toLowerCase().includes(term) ||
        item.stand_category_name?.toLowerCase().includes(term)
      )
    })
  }, [data, search])

  const columns = [
    {
      name: "Exhibition",
      selector: (r: Participation) => r.exhibition_title || "—",
      sortable: true,
      wrap: true,
    },
    {
      name: "Stand Category",
      selector: (r: Participation) => r.stand_category_name || "—",
      sortable: true,
      wrap: true,
    },
    {
      name: "Stands",
      selector: (r: Participation) => r.num_of_stands || "—",
      sortable: true,
      width: "110px",
      center: true,
    },
    {
      name: "Status",
      cell: (r: Participation) => (
        <Badge
          pill
          color={r.status ? "success" : "secondary"}
          className="px-3 py-1"
          style={{ fontWeight: 500 }}
        >
          {r.status ? "Active" : "Inactive"}
        </Badge>
      ),
      sortable: true,
      sortFunction: (a: Participation, b: Participation) => Number(a.status) - Number(b.status),
      width: "130px",
    },
    {
      name: "Actions",
      cell: (r: Participation) => (
        <div className="d-flex gap-2">
          <Btn
            color="info"
            size="xs"
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: "30px", height: "30px" }}
            onClick={() => onEdit(r)}
            title="Edit"
          >
            <FaEdit size={12} />
          </Btn>
          <Btn
            color="danger"
            size="xs"
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{ width: "30px", height: "30px" }}
            onClick={() => handleDelete(r.id)}
            title="Delete"
          >
            <FaTrash size={12} />
          </Btn>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "110px",
    },
  ]

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottomWidth: "2px",
        borderBottomColor: "#e9ecef",
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "0.82rem",
        textTransform: "uppercase" as const,
        letterSpacing: "0.02em",
        color: "#495057",
      },
    },
    rows: {
      style: {
        minHeight: "56px",
      },
    },
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardBody className="p-3 p-md-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-light-success text-success"
              style={{ width: 38, height: 38, backgroundColor: "rgba(40, 167, 69, 0.12)" }}
            >
              <FaLayerGroup size={16} className="text-success" />
            </div>
            <div>
              <h6 className="mb-0 fw-bold">Exhibition Participations</h6>
              <small className="text-muted">
                {data.length} total{search ? ` · ${filteredData.length} matching` : ""}
              </small>
            </div>
          </div>
          <Btn color="primary" size="sm" onClick={onAdd} className="d-flex align-items-center gap-1">
            <FaPlus size={12} /> Add Participation
          </Btn>
        </div>

        <div className="position-relative mb-3" style={{ maxWidth: 340 }}>
          <FaSearch
            size={13}
            className="text-muted position-absolute"
            style={{ left: 14, top: "50%", transform: "translateY(-50%)" }}
          />
          <input
            type="search"
            className="form-control ps-4"
            placeholder="Search by exhibition, category..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{ paddingLeft: 34, borderRadius: "0.5rem" }}
          />
          {search && (
            <button
              type="button"
              onClick={clearSearch}
              className="btn btn-sm position-absolute p-0 d-flex align-items-center justify-content-center"
              style={{
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                width: 22,
                height: 22,
                border: "none",
                color: "#6c757d",
              }}
              title="Clear search"
            >
              <FaTimes size={12} />
            </button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={filteredData}
          progressPending={loading}
          pagination
          highlightOnHover
          pointerOnHover
          customStyles={customStyles}
          noDataComponent={
            <div className="text-center py-5">
              <FaLayerGroup size={28} className="text-muted mb-2" />
              <p className="text-muted mb-0">
                {search ? "No participations match your search." : "No participations found."}
              </p>
            </div>
          }
        />
      </CardBody>
    </Card>
  )
}

export default ParticipationsTab